// DatabaseModalOverlays.jsx

import React from 'react';

// Utilities
import { getNextViewPosition } from 'utilities/ViewOrder';

// Components
import FormDesignModal from './forms/FormDesignModal';
import FormSettingsModal from './forms/FormSettingsModal';
import MissionModal from './missions/MissionModal';
import RollupModal from './rollups/RollupModal';
import Confirm from 'components/confirm/Confirm';
import Button from 'components/button/Button';
import AutomationRuleModal from 'collection/automations/AutomationRuleModal';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalLabel from 'components/modal/label/ModalLabel';
import TextInput from 'components/textinput/TextInput';
import MissionTaskModal from 'missions/taskmodal/MissionTaskModal';
import ViewModal from 'views/modal/ViewModal';
import WorkflowDesignModal from 'workflows/designer/WorkflowDesignModal';
import { useIsMobile } from 'hooks/useDeviceDetect';

/**
 * DatabaseModalOverlays
 *
 * Renders the nested editors and confirmation dialogs used by DatabaseModal.
 *
 * @returns {ReactNode} The rendered nested modals and confirmations.
 */
const DatabaseModalOverlays = ({
  viewEditorOpen,
  setViewEditorOpen,
  viewEditorMode = 'create',
  typeKey,
  baseKey,
  typeLabel = '',
  views,
  editingView,
  viewGroups = [],
  handleViewSaved,
  automationEditorOpen,
  setAutomationEditorOpen,
  editingAutomation,
  automationEditorMode,
  isAutomationSaving,
  fieldOptions,
  ruleFieldOptions,
  fieldTypeByKey,
  fieldEnumOptionsByKey,
  nodeTypeOptions,
  handleSaveAutomation,
  handleDeleteAutomation,
  missionTemplateEditorOpen,
  setMissionTemplateEditorOpen,
  missionTemplateEditorMode,
  selectedMissionTemplateKey,
  isMissionSaving,
  missionTitle,
  missionDescription,
  missionIconKey,
  missionMetaError,
  canSaveMissionMeta,
  setMissionTitle,
  setMissionDescription,
  setMissionIconKey,
  handleSaveMissionMeta,
  handleDeleteMissionTemplate,
  handleAddMissionTask,
  handleEditMissionTask,
  handleReorderMissionTask,
  handleDesignMissionTemplate,
  handleSaveMissionDesign,
  missionDesignOpen,
  setMissionDesignOpen,
  closeMissionDesign,
  selectedMissionTemplate,
  missionEditorOpen,
  setMissionEditorOpen,
  missionTasks,
  missionEdges = [],
  editingMissionTask,
  resolvedProperties,
  handleSaveMissionTask,
  handleDeleteMissionTask,
  rollupEditorOpen,
  setRollupEditorOpen,
  editingRollup,
  schemaFields,
  effectiveTypeKey,
  handleSaveRollup,
  handleDeleteRollup,
  deleteConfirmOpen,
  setDeleteConfirmOpen,
  deleteConfirmMessage,
  deleteFinalConfirmOpen,
  setDeleteFinalConfirmOpen,
  handleDelete,
  formDesignOpen,
  closeFormDesign,
  editingForm,
  formsPreviewNodeKey,
  formCreateModalOpen,
  setFormCreateModalOpen,
  formCreateTitleDraft,
  setFormCreateTitleDraft,
  isFormCreating,
  handleCreateForm,
  formSettingsOpen,
  setFormSettingsOpen,
  formSettingsDraft,
  setFormSettingsDraft,
  canSaveFormSettings,
  handleSaveFormSettings,
  handleDeleteForm,
  isFormSaving,
}) => {

  // ----- STATE ----- //

  const [formDeleteConfirmOpen, setFormDeleteConfirmOpen] = React.useState(false);
  const isMobile = useIsMobile();
  const canCreateForm = Boolean(String(formCreateTitleDraft || '').trim()) && !isFormCreating;

  // ----- RENDER ----- //

  return (
    <>
      <ViewModal
        isOpen={viewEditorOpen}
        setOpen={setViewEditorOpen}
        typeKey={typeKey}
        baseKey={baseKey}
        createdFrom="DATABASE"
        createPosition={getNextViewPosition(views)}
        mode={viewEditorMode === 'edit' ? 'edit' : 'create'}
        view={editingView}
        viewGroups={viewGroups}
        initialViewType={editingView?.type || 'LIST'}
        onSave={handleViewSaved}
      />

      <AutomationRuleModal
        isOpen={automationEditorOpen}
        setOpen={setAutomationEditorOpen}
        rule={editingAutomation}
        mode={automationEditorMode}
        isSaving={isAutomationSaving}
        fieldOptions={fieldOptions}
        ruleFieldOptions={ruleFieldOptions}
        fieldTypeByKey={fieldTypeByKey}
        fieldEnumOptionsByKey={fieldEnumOptionsByKey}
        baseKey={baseKey}
        nodeTypeOptions={nodeTypeOptions}
        onDelete={handleDeleteAutomation}
        onSave={handleSaveAutomation}
      />

      <MissionModal
        canSaveMissionMeta={canSaveMissionMeta}
        isOpen={missionTemplateEditorOpen}
        isSaving={isMissionSaving}
        missionDescription={missionDescription}
        missionIconKey={missionIconKey}
        missionMetaError={missionMetaError}
        missionTasks={missionTasks}
        missionTitle={missionTitle}
        mode={missionTemplateEditorMode}
        selectedMissionTemplateKey={selectedMissionTemplateKey}
        setOpen={setMissionTemplateEditorOpen}
        onAddMissionTask={handleAddMissionTask}
        onDeleteMissionTemplate={() => handleDeleteMissionTemplate(selectedMissionTemplateKey)}
        onDesignWorkflow={() => handleDesignMissionTemplate(selectedMissionTemplate)}
        onEditMissionTask={handleEditMissionTask}
        onMissionDescriptionChange={setMissionDescription}
        onMissionIconKeyChange={setMissionIconKey}
        onMissionTitleChange={setMissionTitle}
        onReorderMissionTask={handleReorderMissionTask}
        onSaveMissionMeta={handleSaveMissionMeta}
      />

      <WorkflowDesignModal
        availableFields={resolvedProperties}
        baseKey={baseKey}
        isOpen={missionDesignOpen}
        isSaving={isMissionSaving}
        recordTypeOptions={nodeTypeOptions}
        template={selectedMissionTemplate}
        typeLabel={typeLabel}
        onClose={closeMissionDesign || (() => setMissionDesignOpen(false))}
        onDelete={handleDeleteMissionTemplate}
        onSave={handleSaveMissionDesign}
      />

      <MissionTaskModal
        isOpen={missionEditorOpen}
        setOpen={setMissionEditorOpen}
        mode={editingMissionTask ? 'edit' : 'create'}
        task={editingMissionTask}
        availableTasks={missionTasks}
        availableFields={resolvedProperties}
        edges={missionEdges}
        recordTypeOptions={nodeTypeOptions}
        typeLabel={typeLabel}
        baseKey={baseKey}
        onDelete={() => handleDeleteMissionTask(editingMissionTask?.key)}
        onSave={handleSaveMissionTask}
      />

      <RollupModal
        isOpen={rollupEditorOpen}
        setOpen={setRollupEditorOpen}
        mode={editingRollup ? 'edit' : 'create'}
        rollup={editingRollup}
        fields={schemaFields}
        typeKey={effectiveTypeKey}
        onDelete={handleDeleteRollup}
        onSave={handleSaveRollup}
      />

      <FormDesignModal
        formDoc={editingForm}
        isOpen={formDesignOpen}
        nodeKey={formsPreviewNodeKey}
        title={editingForm?.title ? `${editingForm.title} - Form Designer` : 'Design Form Template'}
        onClose={closeFormDesign}
      />

      <Modal
        iconKey="dynamic_form"
        isOpen={formCreateModalOpen}
        title="Add Form Template"
        width={isMobile ? '100%' : 480}
        onClose={() => setFormCreateModalOpen(false)}
      >
        <ModalForm>
          <ModalField>
            <ModalLabel label="Title" />
            <TextInput
              value={formCreateTitleDraft}
              placeholder="Enter a title..."
              onChange={(event) => setFormCreateTitleDraft?.(event.target.value)}
            />
          </ModalField>
        </ModalForm>

        <ModalButtons>
          <ModalButtonSection>
            <Button
              disabled={isFormCreating}
              iconKey="close"
              label="Cancel"
              addon="secondary"
              onClick={() => setFormCreateModalOpen(false)}
            />
            <Button
              disabled={!canCreateForm}
              iconKey="check"
              label={isFormCreating ? 'Creating...' : 'Create Form Template'}
              addon="primary"
              onClick={handleCreateForm}
            />
          </ModalButtonSection>
        </ModalButtons>
      </Modal>

      <FormSettingsModal
        canSave={canSaveFormSettings}
        description={formSettingsDraft?.description || ''}
        isOpen={formSettingsOpen}
        isSaving={isFormSaving}
        title={formSettingsDraft?.title || ''}
        onClose={() => setFormSettingsOpen(false)}
        onDelete={formSettingsDraft?.isDefault ? undefined : () => setFormDeleteConfirmOpen(true)}
        onDescriptionChange={(nextDescription) => {
          setFormSettingsDraft((prev) => ({
            ...prev,
            description: String(nextDescription || ''),
          }));
        }}
        onSave={handleSaveFormSettings}
        onTitleChange={(nextTitle) => {
          setFormSettingsDraft((prev) => ({
            ...prev,
            title: String(nextTitle || ''),
          }));
        }}
      />

      <Confirm
        title="Delete Form Template?"
        message="This will delete the form template. Records will no longer show this form on the Forms tab. This cannot be undone."
        isOpen={formDeleteConfirmOpen}
        setOpen={setFormDeleteConfirmOpen}
        onConfirm={async () => {
          setFormDeleteConfirmOpen(false);
          await handleDeleteForm();
        }}
      />

      <Confirm
        title="Delete Database"
        message={deleteConfirmMessage}
        isOpen={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        onConfirm={() => {
          setDeleteConfirmOpen(false);
          setDeleteFinalConfirmOpen(true);
        }}
      />

      <Confirm
        title="Final Confirmation"
        message="Just making sure... we don't want you to accidentally delete anything important to you."
        isOpen={deleteFinalConfirmOpen}
        setOpen={setDeleteFinalConfirmOpen}
        onConfirm={handleDelete}
      />
    </>
  );
};

// Dev Tools
DatabaseModalOverlays.displayName = 'DatabaseModalOverlays';

export default DatabaseModalOverlays;
